<script setup lang="ts">
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useForm } from 'vee-validate'
import * as validation from 'zod'
import { toTypedSchema } from '@vee-validate/zod'
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import EmailIcon from '@/assets/icons/email.svg'

const props = defineProps<{
  email: string
  onEmailChange: (email: string) => void
  isCodeSent: boolean
  countdown: number
  errors: Record<string, string>
  customClass?: string
}>()

const { t } = useI18n()

const formSchema = toTypedSchema(
  validation.object({
    email: validation.string().email(t('Errors.label.InvalidEmail'))
  })
)

interface FormValues {
  email: string
}

const { values, errors, handleSubmit } = useForm<FormValues>({
  validationSchema: formSchema,
  initialValues: {
    email: props.email
  },
})

watch(
  () => values.email,
  (newEmail) => {
    if (newEmail) {
      props.onEmailChange(newEmail)
    }
  },
  { immediate: true }
)

const onSubmit = handleSubmit((values) => {
  emit('submit', values.email)
})

const emit = defineEmits<{
  (e: 'submit', email: string): void
}>()
</script>

<template>
  <form
    class="w-full flex flex-col gap-4 h-full"
    :class="customClass"
    @submit.prevent="onSubmit"
  >
    <FormField v-slot="{ componentField }" name="email">
      <FormItem>
        <FormControl>
          <div class="relative">
            <span class="absolute left-4 top-1/2 -translate-y-1/2">
              <EmailIcon />
            </span>
            <Input
              v-bind="componentField"
              type="text"
              :disabled="isCodeSent"
              :placeholder="t('LoginPopup.placeholder.EnterEmail')"
              :variant="countdown > 0 && countdown < 10 ? 'disabled' : 'formField'"
              :class="` 
                pl-12
                text-white/60
                h-[48px]
                text-sm
                focus:border-[#0071F2] focus:outline-none
                bg-[#10192E]
                ${errors.email ? 'border-error' : 'border-[#1D3660]'}
                outline-none w-full mt-0!`"
            />
          </div>
          <FormMessage class="mt-0! font-light! text-[12px]! leading-[18px]!" />
        </FormControl>
      </FormItem>
    </FormField>

    <div class="flex gap-2 justify-end">
      <slot name="buttons" :disabled="!values.email || errors.email" />
    </div>

    <slot name="footer" />
  </form>
</template> 